/**
 * Coordinates PWA service-worker updates with live, interruption-sensitive
 * sessions (roleplay/objection/speed-sell/cold-call recordings and realtime
 * voice conversations).
 *
 * While a "critical session" is active we must never surface — let alone apply
 * — a service-worker update, because doing so reloads the page and destroys the
 * MediaRecorder, AudioContext and realtime WebRTC connection mid-conversation.
 */

type Listener = () => void;

let activeSessionCount = 0;
const listeners = new Set<Listener>();

function notify(): void {
  listeners.forEach((listener) => listener());
}

/** True while at least one critical session is in progress. */
export function isSessionActive(): boolean {
  return activeSessionCount > 0;
}

/**
 * Mark a critical session as started. Returns a release function that must be
 * called exactly once when the session ends. Reference-counted so concurrent
 * sessions (e.g. recording + realtime connection) compose correctly.
 */
export function beginCriticalSession(): () => void {
  activeSessionCount += 1;
  let released = false;

  return () => {
    if (released) return;
    released = true;
    activeSessionCount = Math.max(0, activeSessionCount - 1);
    if (activeSessionCount === 0) notify();
  };
}

/** Subscribe to changes; fires whenever the last active session ends. */
export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
